import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { openModal } from 'reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import Tooltip from 'components/Tooltip';
import './QuicklinksSection.css';
import { getDownloadLinks } from 'utils/propertyLinks';

const QuicklinksSection = memo(
	({ propertyId, propertyAddress, propertyData, onQuickLinkClick }) => {
		const dispatch = useDispatch();

		// Helper function to open modal with iframe
		const openIframeModal = (url, title) => {
			dispatch(openModal(MODAL_TYPES.IFRAME, { url, title }));
		};

		// Helper function to open FDNY report modal
		const openFdnyReportModal = useCallback(
			() =>
				dispatch(
					openModal(MODAL_TYPES.FDNY_REPORT, {
						address: propertyData?.address,
						bin: propertyData?.bin,
					})
				),
			[propertyData?.address, propertyData?.bin, dispatch]
		);

		// Build the file buttons data similar to BuildingInfoTableV2
		const cooLink =
			getDownloadLinks(propertyData)?.['Certificate of Occupancy'];

		const hpd_building_id = propertyData?.hpd_building_id;
		const lat = propertyData?.lat;
		const longitude = propertyData?.['long'];
		const landmarkLink = `https://nyclpc.maps.arcgis.com/apps/webappviewer/index.html?id=93a88691cace4067828b1eede432022b&marker=${longitude},${lat},,,,&markertemplate={"title":"","longitude":${longitude},"latitude":${lat},"isIncludeShareUrl":true}&level=18`;

		// Create fileButtons array matching BuildingInfoTableV2 structure
		const fileButtons = [
			{
				id: 'coo',
				title: 'Certificate of Occupancy',
				icon: '/file-image-nyc-buildings.png',
				tooltipText: 'Certificate of Occupancy',
				action: () => {
					// Note: This would need the actual COO link from getDownloadLinks
					// For now, navigate to certificates page
					openIframeModal(cooLink, 'Certificate of Occupancy');
					// onQuickLinkClick('certificates', propertyId);
				},
				available: true, // Could be conditional based on actual COO link
			},
			{
				id: 'fdny',
				title: 'FDNY Building Information Profile',
				icon: '/file-image-fdny.png',
				tooltipText: 'FDNY Building Information Profile',
				action: openFdnyReportModal,
				available: true,
			},
			{
				id: 'hpd',
				title: 'I-Card (HPD)',
				icon: '/file-image-hpd.png',
				tooltipText: 'I-Card (HPD)',
				action: () => dispatch(openModal(MODAL_TYPES.HPD_I_CARDS)),
				available: !!hpd_building_id,
			},
			{
				id: 'landmarks',
				title: 'LPC Designation',
				icon: '/file-image-landmarks.png',
				tooltipText: 'LPC Designation',
				action: () => openIframeModal(landmarkLink, 'LPC Designation'),
				available: !!(landmarkLink && lat && longitude),
			},
		];

		// Filter available buttons
		const availableButtons = fileButtons.filter((button) => button.available);

		return (
			<div className="quicklinks-section">
				<div className="property-file-icon-links">
					{availableButtons.map((button) => (
						<Tooltip key={button.id} text={button.tooltipText}>
							<button
								className="btn-hyperlink btn btn-sm file-icon-button"
								onClick={button.action}
								title={button.tooltipText}
							>
								<img
									className="quicklink-icon-img"
									src={button.icon}
									alt={button.title}
								/>
							</button>
						</Tooltip>
					))}
				</div>
				{availableButtons.length === 0 && (
					<div className="no-quicklinks-message">
						<i className="bi bi-info-circle"></i>
						<span>No quicklinks available for this property</span>
					</div>
				)}
			</div>
		);
	}
);

QuicklinksSection.propTypes = {
	propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
		.isRequired,
	propertyAddress: PropTypes.string,
	propertyData: PropTypes.object,
	onQuicklinkClick: PropTypes.func.isRequired,
};

QuicklinksSection.defaultProps = {
	propertyAddress: '',
	propertyData: {},
};

QuicklinksSection.displayName = 'QuicklinksSection';

export default QuicklinksSection;
